import { CRM_OBJECT } from 'customer-data-objects/constants/CrmObjectType';
import { List as ImmutableList, fromJS } from 'immutable';
// @ts-expect-error untyped module
import { Metric } from '../../config/metrics';
import { GAUGE, TABLE } from '../../constants/chartTypes';
import { AGGREGATION, SEARCH } from '../../constants/configTypes';
import { DAY } from '../../constants/frequency';
import { DESC } from '../../constants/sortOrder';
// @ts-expect-error untyped module
import { isSupportedCrmObject } from '../../crmObjects/utils';
import { dataTypeSettings } from '../../retrieve/inboundDb/common/dataTypes';
import { isSupportedUnifiedEventQLDataType } from '../../unifiedEventQL/utils';
import { getIdProperties } from '../../utils/getIdProperties';
import { getConfigReportLimit, getConfigType, getCustomized, getDataType, getDisplayParamsDataColumns, getFilters, getFrequency, getOffset, getReportConfig } from '../configReportGetters';
import { getActive, getBusinessUnitId, getCreatedAt, getName, getReportChartType, getReportId, getReportPortalId, getUserReportPermissionLevel, isFlpRestricted } from '../reportGetters';
import { UNIFIED_EVENTS_QL } from '../../constants/dataTypes';
function getMetricsForSearchReport(report) {
  var _getDisplayParamsData;
  const visibleObjectIds = getIdProperties(report);
  return ImmutableList((_getDisplayParamsData = getDisplayParamsDataColumns(report)) !== null && _getDisplayParamsData !== void 0 ? _getDisplayParamsData : []).unshift(...visibleObjectIds.toArray()).map(property => Metric({
    property
  }));
}
function getDefaultSortForSearchReport(report) {
  const dataType = getDataType(report);
  const {
    defaultDateProperty,
    idColumn
  } = isSupportedCrmObject(dataType) || isSupportedUnifiedEventQLDataType(dataType) ? {
    defaultDateProperty: 'hs_createdate',
    idColumn: ''
  } : dataTypeSettings[dataType];
  const defaultSortProperty = defaultDateProperty || idColumn;
  const sort = defaultSortProperty ? {
    property: defaultSortProperty,
    order: DESC
  } : undefined;
  return sort ? fromJS([sort]) : ImmutableList();
}
function getDataTypeForReportConversion(dataType) {
  if (isSupportedCrmObject(dataType)) {
    return CRM_OBJECT;
  }
  if (isSupportedUnifiedEventQLDataType(dataType)) {
    return UNIFIED_EVENTS_QL;
  }
  return dataType;
}

/**
 * Converts a report into a Search report. This is NOT meant to reverse the transformation done in convertFromSearchReport
 * as properties will be lost.
 */
export function convertToSearchReport(report, dataPageSort) {
  var _getDisplayParamsData2;
  if (getConfigType(report) === SEARCH) {
    return report.setIn(['config', 'metrics'], getMetricsForSearchReport(report));
  }
  const dataType = getDataType(report);
  const originalConfig = getReportConfig(report);
  return fromJS({
    id: getReportId(report),
    // ReportId will always return a number for ConfigReports
    name: getName(report),
    flpRestricted: isFlpRestricted(report),
    chartType: TABLE,
    active: getActive(report),
    createdAt: getCreatedAt(report),
    portalId: getReportPortalId(report),
    businessUnitId: getBusinessUnitId(report),
    userReportPermissionLevel: getUserReportPermissionLevel(report),
    displayParams: {
      hideKeyColumn: true,
      dataColumns: (_getDisplayParamsData2 = getDisplayParamsDataColumns(report)) !== null && _getDisplayParamsData2 !== void 0 ? _getDisplayParamsData2 : []
    },
    config: {
      customized: getCustomized(report),
      configType: SEARCH,
      dataType: getDataTypeForReportConversion(dataType),
      objectTypeId: isSupportedCrmObject(dataType) ? dataType : undefined,
      unifiedEventTypeName: isSupportedUnifiedEventQLDataType(dataType) ? dataType : undefined,
      filters: getFilters(originalConfig),
      frequency: getFrequency(originalConfig),
      dimensions: dataTypeSettings[dataType] ? dataTypeSettings[dataType].tableDimensions || [] : [],
      metrics: getMetricsForSearchReport(report),
      limit: getConfigReportLimit(report),
      sort: dataPageSort && dataPageSort.size > 0 ? dataPageSort : getDefaultSortForSearchReport(report),
      processors: [],
      offset: getOffset(report)
    }
  });
}

/**
 * Converts a Search report into an Aggregation report. This is NOT meant to reverse
 * the transformation done in convertToSearchReport as properties will be lost.
 */
export function convertFromSearchReport(report) {
  if (getConfigType(report) !== SEARCH) {
    return report;
  }
  const dataType = getDataType(report);
  const originalConfig = getReportConfig(report);
  const chartType = getReportChartType(report);
  const isGaugeChart = chartType === GAUGE;
  const updatedReport = fromJS({
    id: getReportId(report),
    // ReportId will always return a number for ConfigReports
    name: getName(report),
    flpRestricted: isFlpRestricted(report),
    chartType: !isGaugeChart ? chartType : undefined,
    active: getActive(report),
    createdAt: getCreatedAt(report),
    portalId: getReportPortalId(report),
    businessUnitId: getBusinessUnitId(report),
    userReportPermissionLevel: getUserReportPermissionLevel(report),
    displayParams: {
      customWidget: {
        type: 'DATA_LAYER'
      },
      dataColumns: getDisplayParamsDataColumns(report)
    },
    config: {
      customized: getCustomized(report),
      configType: AGGREGATION,
      dataType: getDataTypeForReportConversion(dataType),
      objectTypeId: isSupportedCrmObject(dataType) ? dataType : undefined,
      unifiedEventTypeName: isSupportedUnifiedEventQLDataType(dataType) ? dataType : undefined,
      filters: getFilters(originalConfig),
      frequency: DAY,
      metrics: [],
      processors: [],
      limit: getConfigReportLimit(report),
      sort: [],
      dimensions: [],
      offset: getOffset(report)
    }
  });
  if (isGaugeChart) {
    return updatedReport.setIn(['chart', 'chartType'], GAUGE);
  }
  return updatedReport;
}